import { useCallback } from "react";
import { useConfirm } from "material-ui-confirm";
import { useTranslation } from "react-i18next";

export type DeleteConfirmOptions = {
  itemName?: string;
  titleKey?: string;
  descriptionKey?: string;
  descriptionValues?: Record<string, unknown>;
};

export function isConfirmCancelled(error: unknown): boolean {
  return (error as { name?: string })?.name === "CancelError";
}

export function resolveDeleteItemName(item: unknown): string | undefined {
  if (!item || typeof item !== "object") return undefined;
  const row = item as Record<string, unknown>;
  for (const key of ["name", "title", "label", "fileName", "indexName", "fieldPattern"]) {
    const value = row[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

export function useDeleteConfirm() {
  const confirm = useConfirm();
  const { t } = useTranslation();

  const confirmDelete = useCallback(
    async (options: DeleteConfirmOptions = {}) => {
      const {
        itemName,
        titleKey = "confirm.deleteTitle",
        descriptionKey = "confirm.deleteDescription",
        descriptionValues,
      } = options;

      const values = { name: itemName, ...descriptionValues };
      await confirm({
        title: t(titleKey, values),
        description:
          itemName || descriptionValues
            ? t(descriptionKey, values)
            : t("confirm.deleteDescriptionGeneric"),
        confirmationText: t("common.delete"),
        cancellationText: t("common.cancel"),
        confirmationButtonProps: { color: "error", variant: "contained" },
      });
    },
    [confirm, t]
  );

  return { confirmDelete, isCancelled: isConfirmCancelled };
}
